/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import concat from "lodash/fp/concat";

import { RequestStatus } from "~/constants";
import {
  getPromoToolSitesAsync,
  postCreateAfnoDetailsAsync,
  postDeleteAfnoDetailsAsync,
  postUpdateAfnoDetailsAsync,
} from "~/features/promoTools/thunks";
import transformAfnoEntity from "~/features/promoTools/transformers/transformAfnoEntity";
import { AfnoDetails, AfnoEntity, PromoToolsSite, PromoToolsState } from "~/features/promoTools/types";

export const initialState: PromoToolsState = {
  meta: {
    initStatus: RequestStatus.IDLE,
    createAfnoStatus: RequestStatus.IDLE,
    updateAfnoStatus: RequestStatus.IDLE,
    deleteAfnoStatus: RequestStatus.IDLE,
  },
  data: {
    selectedSite: {
      afnoPrefix: "1-",
      domain: "",
      gender: "",
      siteId: 0,
      referrerId: 0,
      afnoDetails: [],
      unmappedIds: [],
    },
    randomTrackingId: "",
    trackingId: "",
    sites: [],
  },
};

const slice = createSlice({
  name: "promoTools",
  initialState,
  reducers: {
    setSite: (state, action: PayloadAction<PromoToolsSite>) => {
      state.data.selectedSite = action.payload;
    },

    setTrackingId: (state, action: PayloadAction<string>) => {
      state.data.trackingId = action.payload;
    },

    addAfnoDetails: (state, action: PayloadAction<AfnoEntity>) => {
      const entity = action.payload;
      const details = transformAfnoEntity.toAfnoDetails(entity);

      const index = state.data.sites.findIndex(
        (site) => site.siteId === entity.siteid && site.referrerId === entity.referrerid,
      );

      if (index !== -1) {
        const newDetails = concat(details, state.data.sites[index].afnoDetails);
        state.data.sites[index].afnoDetails = newDetails;
        state.data.selectedSite.afnoDetails = newDetails;
      }
    },

    updateAfnoDetails: (state, action: PayloadAction<AfnoEntity>) => {
      const entity = action.payload;
      const details = transformAfnoEntity.toAfnoDetails(entity);

      const index = state.data.sites.findIndex(
        (site) => site.siteId === entity.siteid && site.referrerId === entity.referrerid,
      );

      const detailIndex = state.data.sites[index].afnoDetails.findIndex((detail) => detail.id === entity.id);

      if (index !== -1 && detailIndex !== -1) {
        state.data.sites[index].afnoDetails[detailIndex] = details;
        state.data.selectedSite.afnoDetails[detailIndex] = details;
        state.data.selectedSite.unmappedIds = state.data.selectedSite.unmappedIds.filter(
          (id) => id !== String(entity.trackingid),
        );
      }
    },

    deleteAfnoDetails: (state, action: PayloadAction<{ details: AfnoDetails; siteId: number; referrerId: number }>) => {
      const index = state.data.sites.findIndex(
        (site) => site.siteId === action.payload.siteId && site.referrerId === action.payload.referrerId,
      );

      const detailIndex = state.data.sites[index].afnoDetails.findIndex(
        (detail) => detail.id === action.payload.details.id,
      );

      if (index !== -1 && detailIndex !== -1) {
        state.data.sites[index].afnoDetails[detailIndex].mapped = 1;
        state.data.selectedSite.afnoDetails[detailIndex].mapped = 1;
        state.data.selectedSite.unmappedIds = [
          ...state.data.selectedSite.unmappedIds,
          String(action.payload.details.trackingId),
        ];
      }
    },
  },
  extraReducers: (builder) => {
    builder

      // GET PROMO TOOL SITES
      .addCase(getPromoToolSitesAsync.pending, (state) => {
        state.meta.initStatus = RequestStatus.PENDING;
      })

      .addCase(getPromoToolSitesAsync.fulfilled, (state, action) => {
        state.meta.initStatus = RequestStatus.SUCCESS;
        state.data.sites = action.payload.sites;
        state.data.randomTrackingId = action.payload.randomTrackingId;
        state.data.trackingId = action.payload.trackingId;
        state.data.selectedSite = action.payload.selectedSite ?? initialState.data.selectedSite;
      })

      .addCase(getPromoToolSitesAsync.rejected, (state) => {
        state.meta.initStatus = RequestStatus.ERROR;
      })
      // CREATE AFNO DETAILS
      .addCase(postCreateAfnoDetailsAsync.pending, (state) => {
        state.meta.createAfnoStatus = RequestStatus.PENDING;
      })

      .addCase(postCreateAfnoDetailsAsync.fulfilled, (state) => {
        state.meta.createAfnoStatus = RequestStatus.SUCCESS;
      })

      .addCase(postCreateAfnoDetailsAsync.rejected, (state) => {
        state.meta.createAfnoStatus = RequestStatus.ERROR;
      })

      // UPDATE AFNO DETAILS
      .addCase(postUpdateAfnoDetailsAsync.pending, (state) => {
        state.meta.updateAfnoStatus = RequestStatus.PENDING;
      })

      .addCase(postUpdateAfnoDetailsAsync.fulfilled, (state) => {
        state.meta.updateAfnoStatus = RequestStatus.SUCCESS;
      })

      .addCase(postUpdateAfnoDetailsAsync.rejected, (state) => {
        state.meta.updateAfnoStatus = RequestStatus.ERROR;
      })

      // DELETE AFNO DETAILS
      .addCase(postDeleteAfnoDetailsAsync.pending, (state) => {
        state.meta.deleteAfnoStatus = RequestStatus.PENDING;
      })

      .addCase(postDeleteAfnoDetailsAsync.fulfilled, (state) => {
        state.meta.deleteAfnoStatus = RequestStatus.SUCCESS;
      })

      .addCase(postDeleteAfnoDetailsAsync.rejected, (state) => {
        state.meta.deleteAfnoStatus = RequestStatus.ERROR;
      });
  },
});

export const PromoToolsActions = slice.actions;

export const promoToolsReducer = slice.reducer;
